import type { Module } from 'vuex';

import { am } from './utils';

import type { ConflictResponse, CreateGuidelineRequest, Guideline, GuidelineRevision, GuidelinesConfig, GuidelineSummary, UpdateGuidelineRequest } from '@/types/api/guidelines';
import httpClient from '@/utils/httpClient';
import type { HttpError } from '@/utils/httpClient';

export interface GuidelinesState {
  nodes: GuidelineSummary[];
  config: GuidelinesConfig | null;
  isLoading: boolean;
  hasFailed: boolean;
}

export interface UploadPayload {
  organizationId: string;
  parentId: string | null;
  files: File[];
}

export interface RestorePayload {
  id: string;
  revision: number;
  version: number;
}

const GET_TREE = am('GET_GUIDELINES_TREE');
const baseUrl = '/api/organizations/knowledge';

function required<T>(value: T | undefined | null): T {
  if (value === undefined || value === null) {
    throw new Error('Request failed');
  }
  return value;
}

function toSummary(node: Guideline | GuidelineSummary): GuidelineSummary {
  const summary: Partial<Guideline> = { ...node };
  delete summary.fields;
  delete summary.content;
  delete summary.markdown;
  return summary as GuidelineSummary;
}

export const isHttpError = (error: unknown): error is HttpError => !!error && typeof error === 'object' && 'response' in error && error.response instanceof Response;

export async function readConflictVersion(error: unknown): Promise<number | null> {
  if (!isHttpError(error)) {
    return null;
  }
  try {
    const body = (await error.response.clone().json()) as Partial<ConflictResponse>;
    return Number.isInteger(body?.currentVersion) ? (body.currentVersion as number) : null;
  } catch {
    return null;
  }
}

const guidelines: Module<GuidelinesState, unknown> = {
  namespaced: true,
  state: (): GuidelinesState => ({
    nodes: [],
    config: null,
    isLoading: false,
    hasFailed: false
  }),
  mutations: {
    [GET_TREE.STARTED](state: GuidelinesState) {
      state.isLoading = true;
      state.hasFailed = false;
    },
    [GET_TREE.COMPLETED](state: GuidelinesState, nodes: GuidelineSummary[]) {
      state.isLoading = false;
      state.nodes = nodes;
    },
    [GET_TREE.FAILED](state: GuidelinesState) {
      state.isLoading = false;
      state.hasFailed = true;
    },
    CONFIG_LOADED(state: GuidelinesState, config: GuidelinesConfig) {
      state.config = config;
    },
    NODES_SAVED(state: GuidelinesState, nodes: Array<Guideline | GuidelineSummary>) {
      for (const node of nodes) {
        const summary = toSummary(node);
        const index = state.nodes.findIndex(item => item.id === node.id);
        if (index < 0) {
          state.nodes.push(summary);
        } else {
          state.nodes[index] = summary;
        }
      }
    },
    NODE_DELETED(state: GuidelinesState, id: string) {
      state.nodes = state.nodes.filter(item => item.id !== id && !item.ancestors.includes(id));
    }
  },
  actions: {
    async loadTree({ commit }): Promise<void> {
      commit(GET_TREE.STARTED);
      try {
        commit(GET_TREE.COMPLETED, required(await httpClient.get<GuidelineSummary[]>(`${baseUrl}/tree`)));
      } catch {
        commit(GET_TREE.FAILED);
      }
    },
    async loadConfig({ commit, state }): Promise<GuidelinesConfig> {
      if (!state.config) {
        commit('CONFIG_LOADED', required(await httpClient.get<GuidelinesConfig>(`${baseUrl}/config`)));
      }
      return state.config as GuidelinesConfig;
    },
    async getById(_context, id: string): Promise<Guideline> {
      return required(await httpClient.get<Guideline>(`${baseUrl}/${id}`));
    },
    async getRevisions(_context, id: string): Promise<GuidelineRevision[]> {
      return required(await httpClient.get<GuidelineRevision[]>(`${baseUrl}/${id}/revisions`));
    },
    async create({ commit, dispatch }, payload: CreateGuidelineRequest): Promise<Guideline> {
      const node = required(await httpClient.post<Guideline>(baseUrl, payload));
      commit('NODES_SAVED', [node]);
      if (node.type === 'attorney') {
        await dispatch('loadTree');
      }
      return node;
    },
    async upload({ commit }, { organizationId, parentId, files }: UploadPayload): Promise<Guideline[]> {
      const body = new FormData();
      body.append('organizationId', organizationId);
      if (parentId) {
        body.append('parentId', parentId);
      }
      files.forEach(file => body.append('files', file));
      const nodes = required(await httpClient.request<Guideline[]>(`${baseUrl}/files`, { method: 'POST', json: false, body }));
      commit('NODES_SAVED', nodes);
      return nodes;
    },
    async downloadFile(_context, id: string): Promise<Blob> {
      return required(await httpClient.request<Blob>(`${baseUrl}/${id}/file`, { method: 'GET', image: true }));
    },
    async downloadMarkdown(_context, id: string): Promise<Blob> {
      return required(await httpClient.request<Blob>(`${baseUrl}/${id}/markdown`, { method: 'GET', image: true }));
    },
    async update({ commit }, { id, ...payload }: UpdateGuidelineRequest & { id: string }): Promise<Guideline> {
      const node = required(await httpClient.patch<Guideline>(`${baseUrl}/${id}`, payload));
      commit('NODES_SAVED', [node]);
      return node;
    },
    async restore({ commit }, { id, revision, version }: RestorePayload): Promise<Guideline> {
      const node = required(await httpClient.post<Guideline>(`${baseUrl}/${id}/revisions/${revision}/restore`, { version }));
      commit('NODES_SAVED', [node]);
      return node;
    },
    async remove({ commit }, id: string): Promise<void> {
      required(await httpClient.delete<string>(`${baseUrl}/${id}`));
      commit('NODE_DELETED', id);
    }
  }
};

export default guidelines;
